// Project export — produce a portable JSON file describing a project.
//
// The file is deliberately a thin envelope around the canonical
// `normalizeProjectSnapshot` shape. Anything that would tie the file to a
// specific Appwrite account / team / session (`$id`, `ownerUserId`,
// membership rows, presence, permissions) is stripped so re-importing the
// file into another account or self-hosted instance does not silently
// inherit identifiers from the source.
//
// Pure pieces (`buildProjectExport`, `buildExportFileName`, the schema
// constant) are kept side-effect free so they are easy to unit-test and
// re-use from non-browser callers in the future. Only
// `exportProjectById` (Appwrite I/O) and `downloadProjectExport` (DOM)
// touch the outside world.

import { getProjectById, loadProjectSnapshot } from "./crud";
import { STORAGE_VERSION, normalizeProjectSnapshot } from "./schema";

// Magic constant a future importer can sniff to refuse the wrong file or
// pick the right migration path. Bump the trailing `vN` when the envelope
// shape changes; the inner snapshot is versioned independently via
// `STORAGE_VERSION`.
export const EXPORT_SCHEMA = "netbase-project-export@v1";

// Resolved at build time via Vite's `define` (see `vite.config.js`).
// Falls back to a literal so Node / Vitest don't blow up when the global
// constant is absent.
const APP_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";

const MAX_FILENAME_SLUG_LENGTH = 60;

/**
 * Build the in-memory export envelope. Pure / synchronous.
 *
 * @param {object|null} project The Appwrite project row (or any object
 *   with a `name`). Only `name` is read; everything else is ignored on
 *   purpose so account-bound fields can never leak into the file.
 * @param {object|null} snapshot Project snapshot (already loaded). Will
 *   be re-normalized as defense-in-depth so a corrupt input still yields
 *   a valid file.
 * @param {object} [options]
 * @param {boolean} [options.includeGeneratedConfigs=false] Include the
 *   `router_final` / `switch_final` cached rendered configs. Off by
 *   default because they can be regenerated from `*_data` and may embed
 *   rendered passwords in clear text.
 * @param {number} [options.now] Override the timestamp (used by tests).
 * @returns {object}
 */
export function buildProjectExport(project, snapshot, options = {}) {
  const { includeGeneratedConfigs = false, now } = options;

  const normalizedSnapshot = normalizeProjectSnapshot(snapshot);
  const exportSnapshot = {
    router_data: normalizedSnapshot.router_data,
    switch_data: normalizedSnapshot.switch_data,
    vlan_data: normalizedSnapshot.vlan_data
  };

  if (includeGeneratedConfigs) {
    exportSnapshot.router_final = normalizedSnapshot.router_final;
    exportSnapshot.switch_final = normalizedSnapshot.switch_final;
  }

  const projectName =
    typeof project?.name === "string" && project.name.trim()
      ? project.name.trim()
      : "Untitled project";

  return {
    schema: EXPORT_SCHEMA,
    appVersion: APP_VERSION,
    storageVersion: STORAGE_VERSION,
    exportedAtMs: typeof now === "number" ? now : Date.now(),
    project: {
      name: projectName
    },
    snapshot: exportSnapshot
  };
}

/**
 * Load a project from Appwrite and produce its export envelope.
 *
 * Read-only by design: never calls `touchProject` so an export does not
 * bump `updatedAtMs` and reorder the user's project list.
 *
 * @param {string} projectId
 * @param {Parameters<typeof buildProjectExport>[2]} [options]
 * @returns {Promise<object>}
 */
export async function exportProjectById(projectId, options = {}) {
  if (!projectId) {
    throw new Error("Project not found.");
  }

  const project = await getProjectById(projectId);
  if (!project) {
    throw new Error("Project not found.");
  }

  const snapshot = await loadProjectSnapshot(projectId);
  return buildProjectExport(project, snapshot, options);
}

/**
 * Build a file system-safe filename for a project export. The slug is
 * lowercase, restricted to `[a-z0-9-_]`, length-clamped, and the
 * timestamp has `:` / `.` replaced so the result is safe on Windows.
 *
 * @param {string} projectName
 * @param {Date} [timestamp]
 * @returns {string}
 */
export function buildExportFileName(projectName, timestamp = new Date()) {
  const rawSlug = String(projectName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_FILENAME_SLUG_LENGTH)
    .replace(/^-+|-+$/g, "");
  const slug = rawSlug || "project";

  const stamp = (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
    ? timestamp
    : new Date()
  )
    .toISOString()
    .replace(/[:.]/g, "-");

  return `netbase-project-${slug}-${stamp}.json`;
}

/**
 * Trigger a browser download for the given export envelope.
 *
 * Defensive: silently no-ops outside of a DOM environment (matches the
 * pattern used by `downloadCsv` in `src/handlers/csv.js`).
 *
 * @param {object} exportObject
 * @param {object} [options]
 * @param {string} [options.filename]
 */
export function downloadProjectExport(exportObject, { filename } = {}) {
  if (typeof document === "undefined" || typeof URL === "undefined") return;
  if (!exportObject || typeof exportObject !== "object") return;

  const payload = JSON.stringify(exportObject, null, 2);
  const blob = new Blob([payload], {
    type: "application/json;charset=utf-8"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download =
    filename ||
    buildExportFileName(exportObject?.project?.name || "project");
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
